// src/contexts/LanguageContext.tsx
import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { sourceLanguageTag, availableLanguageTags, setLanguageTag, languageTag } from '@/paraglide/runtime';
import { useShopWhenAvailable } from '@/providers/ShopProvider';

type LanguageContextType = {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  supportedLanguages: { code: string; name: string; flagIcon: string }[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const readStoredLanguage = (): string | null => {
  try {
    return localStorage.getItem('preferredLanguage');
  } catch {
    return null; // private mode / partitioned iframe storage
  }
};

/**
 * Resolve the visitor's language *synchronously*, before the first render. Doing this in an effect
 * instead would be too late: ShopProvider is a descendant, its effects run first, and it would fetch
 * the shop (product names, descriptions, fee labels — all server-translated) under the untouched
 * source language before we ever got to correct it.
 */
const resolveInitialLanguage = (): string => {
  const isSupported = (lang: string | null): boolean =>
    !!lang && availableLanguageTags.includes(lang as typeof sourceLanguageTag);

  const stored = readStoredLanguage();
  if (isSupported(stored)) {
    return stored as string;
  }

  const browserLanguage = navigator.language.split('-')[0];
  if (isSupported(browserLanguage)) {
    return browserLanguage;
  }

  return sourceLanguageTag;
};

export const LanguageProvider = ({ children, shopSlug }: { children: ReactNode, shopSlug?: string }) => {
  const shopContext = useShopWhenAvailable();
  const shop = shopContext?.shop;
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const initial = resolveInitialLanguage();
    if (initial !== languageTag()) {
      setLanguageTag(initial as typeof sourceLanguageTag);
    }
    return initial;
  });
  const [supportedLanguages, setSupportedLanguages] = useState<{ code: string; name: string; flagIcon: string }[]>([]);

  const changeLanguage = useCallback((language: string) => {
    setLanguageTag(language as typeof sourceLanguageTag);
    setCurrentLanguage(language as typeof sourceLanguageTag);
    try {
      localStorage.setItem('preferredLanguage', language);
    } catch {
      /* best-effort — storage can be blocked when embedded as a widget */
    }
  }, []);

  // Create stable reference for shop language codes to avoid unnecessary effect re-runs
  const shopLanguageCodesKey = useMemo(
    () => shop?.supportedLanguages?.map(l => l.code).join(',') ?? '',
    [shop?.supportedLanguages]
  );

  useEffect(() => {
    if (shop && Array.isArray(shop.supportedLanguages)) {
      setSupportedLanguages(shop.supportedLanguages);
    }
  }, [shop]);

  useEffect(() => {
    const shopLanguageCodes = shopLanguageCodesKey ? shopLanguageCodesKey.split(',') : [];
    const hasShopLanguages = shopLanguageCodes.length > 0;

    // Helper to check if language is valid (must be in both Paraglide and shop's supported languages if available)
    const isLanguageValid = (lang: string) => {
      const inParaglide = availableLanguageTags.includes(lang as typeof sourceLanguageTag);
      return hasShopLanguages ? inParaglide && shopLanguageCodes.includes(lang) : inParaglide;
    };

    // Get fallback language (shop's default or first supported, or Paraglide's source)
    const getFallbackLanguage = () => {
      if (shop?.defaultLanguage && isLanguageValid(shop.defaultLanguage)) {
        return shop.defaultLanguage;
      }
      if (hasShopLanguages) {
        const firstValid = shopLanguageCodes.find(code =>
          availableLanguageTags.includes(code as typeof sourceLanguageTag)
        );
        if (firstValid) return firstValid;
      }
      return sourceLanguageTag;
    };

    const storedLanguage = readStoredLanguage();
    if (storedLanguage && isLanguageValid(storedLanguage)) {
      changeLanguage(storedLanguage);
      return;
    }

    // If stored language is not valid for this shop, try browser language
    const browserLanguage = navigator.language.split('-')[0];
    if (isLanguageValid(browserLanguage)) {
      changeLanguage(browserLanguage);
      return;
    }

    // Fall back to shop's default or first supported language
    changeLanguage(getFallbackLanguage());
  }, [shopSlug, shop?.defaultLanguage, shopLanguageCodesKey, changeLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, supportedLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};